import { getHeaders } from 'auth/auth';
import { createApi } from '@reduxjs/toolkit/query/react';
import baseQueryWithAuth from '../baseQuery';

/**
 * RTK Query API slice for bulk document upload operations
 */
export const bulkUploadAPI = createApi({
  reducerPath: 'bulkUpload',
  tagTypes: ['BulkUploadJob'],
  baseQuery: baseQueryWithAuth,
  endpoints: (builder) => ({
    /**
     * Create a bulk upload job
     * POST /b2b/documents/bulk/upload (B2B) or /b2c/documents/bulk/upload (B2C)
     */
    createBulkUpload: builder.mutation({
      query: ({ formData, isConsumer = false }) => {
        const headers = getHeaders();
        // Remove Content-Type so axios auto-sets multipart/form-data with boundary.
        // Must also be removed from instance defaults for this request.
        delete headers['Content-Type'];
        delete headers['content-type'];

        const endpoint = isConsumer
          ? '/b2c/documents/bulk/upload'
          : '/b2b/documents/bulk/upload';

        return {
          url: endpoint,
          method: 'POST',
          headers,
          body: formData,
          timeout: 1200000, // 20 min — large file uploads need more than the default 30s
        };
      },
      invalidatesTags: ['BulkUploadJob'],
    }),

    /**
     * Get bulk upload job status
     * GET /b2b/documents/bulk/jobs/{jobId} (B2B) or /b2c/documents/bulk/jobs/{jobId} (B2C)
     */
    getBulkUploadStatus: builder.query({
      query: ({ jobId, isConsumer = false }) => {
        const basePath = isConsumer ? '/b2c/documents/bulk' : '/b2b/documents/bulk';
        return {
          url: `${basePath}/jobs/${jobId}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: (_result, _error, { jobId }) => [
        { type: 'BulkUploadJob', id: jobId },
      ],
    }),

    /**
     * Get documents created by a bulk upload job
     * GET /b2b/documents/bulk/jobs/{jobId}/documents (B2B) or /b2c/documents/bulk/jobs/{jobId}/documents (B2C)
     */
    getBulkUploadDocuments: builder.query({
      query: ({ jobId, isConsumer = false }) => {
        const basePath = isConsumer ? '/b2c/documents/bulk' : '/b2b/documents/bulk';
        return {
          url: `${basePath}/jobs/${jobId}/documents`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),

    /**
     * Retry a failed bulk upload job
     * POST /b2b/documents/bulk/jobs/{jobId}/retry
     */
    retryBulkUpload: builder.mutation({
      query: ({ jobId }) => ({
        url: `/b2b/documents/bulk/jobs/${jobId}/retry`,
        method: 'POST',
        headers: getHeaders(),
      }),
      invalidatesTags: (_result, _error, { jobId }) => [
        { type: 'BulkUploadJob', id: jobId },
      ],
    }),

    /**
     * Cancel a bulk upload job
     * POST /b2b/documents/bulk/jobs/{jobId}/cancel (B2B) or /b2c/documents/bulk/jobs/{jobId}/cancel (B2C)
     */
    cancelBulkUpload: builder.mutation({
      query: ({ jobId, isConsumer = false }) => {
        const basePath = isConsumer ? '/b2c/documents/bulk' : '/b2b/documents/bulk';
        return {
          url: `${basePath}/jobs/${jobId}/cancel`,
          method: 'POST',
          headers: getHeaders(),
        };
      },
      invalidatesTags: (_result, _error, { jobId }) => [
        { type: 'BulkUploadJob', id: jobId },
      ],
    }),

    /**
     * Reclassify a document from bulk upload
     * PUT /b2b/documents/{documentId}/reclassify
     */
    reclassifyDocument: builder.mutation({
      query: ({ documentId, documentTypeId, documentCategoryId }) => ({
        url: `/b2b/documents/${documentId}/reclassify`,
        method: 'PUT',
        headers: getHeaders(),
        body: {
          document_type_id: documentTypeId,
          document_category_id: documentCategoryId,
        },
      }),
      invalidatesTags: ['BulkUploadJob'],
    }),
  }),
});

export const {
  useCreateBulkUploadMutation,
  useGetBulkUploadStatusQuery,
  useLazyGetBulkUploadStatusQuery,
  useGetBulkUploadDocumentsQuery,
  useLazyGetBulkUploadDocumentsQuery,
  useRetryBulkUploadMutation,
  useCancelBulkUploadMutation,
  useReclassifyDocumentMutation,
} = bulkUploadAPI;

export default bulkUploadAPI;
